import { BuildingType, type BuildingView, dollars } from './buildings';
import { isNight } from './clock';

/*
 * Staff. Four teams keep the airport running: terminal staff (check-in and security), ground
 * crews (who drive the handling base's vehicles), firefighters, and air traffic controllers in
 * the tower. How many each team needs follows from the buildings; how many are hired is the
 * player's call. Short-staffed teams work at a fraction of their capacity. With automatic
 * staffing (the default) the airport always hires exactly what it needs.
 *
 * Shifts: the head count covers the day. At night (22:00–06:00) each team runs a full, half or
 * no night shift; whoever works nights is paid a premium. A small night crew saves wages on an
 * airport that sleeps, and an airport with night flights needs its tower and fire crew awake.
 */

/** Saved: append only. */
export const StaffRole = { Terminal: 0, Ground: 1, Fire: 2, Atc: 3 } as const;
export type StaffRoleId = (typeof StaffRole)[keyof typeof StaffRole];
export const STAFF_ROLES: readonly StaffRoleId[] = [0, 1, 2, 3];
export const ALL_AUTO = 0b1111;
/** Most people one team can have. */
export const MAX_STAFF = 999;
/** Night shift options: share (percent) of the team on duty at night. */
export const NIGHT_CREW_OPTIONS: readonly number[] = [100, 50, 0];
/** Pay for a night hour, percent of a day hour. */
export const NIGHT_PREMIUM_PERCENT = 125;
/** Night hours per day (22:00–06:00). */
const NIGHT_HOURS = 8;

/**
 * Pay policy: wages as a percent of the going rate. Well-paid people get more done; underpaid
 * ones work less and may strike (a chance each morning): half of every team walks out, unpaid,
 * for a few hours.
 */
export const WAGE_OPTIONS: readonly number[] = [80, 90, 100, 110, 120];
/** Work done per person (percent), by wage option. */
export const WAGE_EFFICIENCY: readonly number[] = [95, 100, 100, 105, 110];
/** Chance (per mille) of a strike each morning, by wage option. */
export const STRIKE_PERMILLE: readonly number[] = [80, 30, 0, 0, 0];
export const STRIKE_HOURS = 6;
export const STRIKE_PERCENT_OUT = 50;

function wageIndex(pct: number): number {
  const i = WAGE_OPTIONS.indexOf(pct);
  return i < 0 ? 2 : i;
}

export function wageEfficiency(pct: number): number {
  return WAGE_EFFICIENCY[wageIndex(pct)] ?? 100;
}

export function strikeChance(pct: number): number {
  return STRIKE_PERMILLE[wageIndex(pct)] ?? 0;
}

/** Pay per person per game day (cents), by role. */
export const SALARY_PER_DAY: readonly number[] = [
  dollars(90),
  dollars(80),
  dollars(110),
  dollars(180),
];

/** People needed per completed level of the building each team works in. */
export const STAFF_PER_LEVEL: readonly number[] = [6, 6, 4, 3];
const WORKPLACE: readonly number[] = [
  BuildingType.Terminal,
  BuildingType.GroundHandling,
  BuildingType.FireStation,
  BuildingType.Tower,
];

/** People each team needs for the buildings as they stand. */
export function requiredStaff(buildings: readonly BuildingView[]): number[] {
  const need = [0, 0, 0, 0];
  for (const b of buildings) {
    if (b.level < 1) continue;
    for (const role of STAFF_ROLES) {
      if (b.type === WORKPLACE[role])
        need[role] = (need[role] ?? 0) + (STAFF_PER_LEVEL[role] ?? 0) * b.level;
    }
  }
  return need;
}

export interface StaffWorld {
  /** People hired per role. */
  staffHired: number[];
  /** Bit per role: hire automatically to what is needed. */
  staffAuto: number;
  /** Per role: percent of the team on the night shift (NIGHT_CREW_OPTIONS). */
  staffNight: number[];
  /** Wages, percent of the going rate (WAGE_OPTIONS), and the end of a strike (tick, 0 = none). */
  wagePct: number;
  strikeUntil: number;
  readonly buildings: readonly BuildingView[];
  readonly time: number;
}

/** People of a team at work now (and paid): everyone by day, the night shift at night, minus strikers. */
export function heads(w: StaffWorld, role: StaffRoleId, night = isNight(w.time)): number {
  const hired = w.staffHired[role] ?? 0;
  // Rounded up: half a team of one is still that one person.
  const shift = night ? Math.ceil((hired * (w.staffNight[role] ?? 100)) / 100) : hired;
  return w.time < w.strikeUntil ? Math.floor((shift * (100 - STRIKE_PERCENT_OUT)) / 100) : shift;
}

/** Work a team gets done now, in people: those at work, scaled by how well they are paid. */
export function onDuty(w: StaffWorld, role: StaffRoleId, night = isNight(w.time)): number {
  return Math.floor((heads(w, role, night) * wageEfficiency(w.wagePct)) / 100);
}

/**
 * How well staffed a team is right now, in per mille (1000 = fully): the people on duty against
 * what the buildings need. Nothing needed counts as full.
 */
export function staffing(
  w: StaffWorld,
  role: StaffRoleId,
  required = requiredStaff(w.buildings),
  night = isNight(w.time),
): number {
  const need = required[role] ?? 0;
  if (need === 0) return 1000;
  return Math.min(1000, Math.floor((onDuty(w, role, night) * 1000) / need));
}

/** Teams on automatic staffing hire (or let go) to exactly what they need. */
export function autoStaff(w: StaffWorld): void {
  const need = requiredStaff(w.buildings);
  for (const role of STAFF_ROLES) {
    if (w.staffAuto & (1 << role)) w.staffHired[role] = Math.min(MAX_STAFF, need[role] ?? 0);
  }
}

/** Salaries for one game hour (cents): the day staff, or the night shift with its premium. */
export function hourlyPayroll(w: StaffWorld): number {
  const night = isNight(w.time);
  let perDay = 0;
  for (const role of STAFF_ROLES) {
    // Whoever is at work is paid, at the airport's wage level; at night with the premium.
    const pay = Math.floor((heads(w, role, night) * (SALARY_PER_DAY[role] ?? 0) * w.wagePct) / 100);
    perDay += night ? Math.floor((pay * NIGHT_PREMIUM_PERCENT) / 100) : pay;
  }
  return Math.floor(perDay / 24);
}

/** A team's wages over a whole day (cents): 16 day hours and 8 night hours. */
export function dailyWages(
  hired: number,
  role: StaffRoleId,
  nightPercent: number,
  wagePct = 100,
): number {
  // The same arithmetic as hourlyPayroll, for this team alone.
  const salary = Math.floor(((SALARY_PER_DAY[role] ?? 0) * wagePct) / 100);
  const nightHeads = Math.ceil((hired * nightPercent) / 100);
  const day = Math.floor((hired * salary) / 24);
  const night = Math.floor(Math.floor((nightHeads * salary * NIGHT_PREMIUM_PERCENT) / 100) / 24);
  return day * (24 - NIGHT_HOURS) + night * NIGHT_HOURS;
}

/** Below this (per mille) the ground crews cannot run the base: the contractor takes over. */
export const GROUND_CONTRACT_BELOW = 250;
/** Below this (per mille) the tower cannot keep a position open: no movements at all. */
export const TOWER_CLOSED_BELOW = 250;
/** Most extra runway separation (ticks) a short-staffed tower adds on each side of a movement. */
export const ATC_MAX_EXTRA_TICKS = 50;

/**
 * Fire-fighting category the station can really provide: one down when a quarter of the crew is
 * missing, none at all below 40 per cent.
 */
export function fireCategory(level: number, permille: number): number {
  if (permille >= 750) return level;
  if (permille >= 400) return Math.max(0, level - 1);
  return 0;
}

/** Extra runway separation (ticks) of a short-staffed tower: fewer controllers, wider gaps. */
export function atcExtraTicks(permille: number): number {
  return Math.floor(((1000 - Math.min(1000, permille)) * ATC_MAX_EXTRA_TICKS) / 1000);
}

/** Vehicles the crews can drive: a share of the fleet, rounded up so a few crews still work. */
export function crewedVehicles(total: number, permille: number): number {
  return Math.ceil((total * Math.min(1000, permille)) / 1000);
}
